// The workbench's model (#471, ADR-0064): what the page knows about a site's
// tag space, one predicate shared by its three views, and the plan a bulk
// action previews and then sends. Nothing here reaches the API; it is pure
// so a test can hold the plan the bar shows against the set the bar sends.
import type { components } from "../../api/schema";
// The pattern grammar is the product's, not the workbench's (#683): `{id}`,
// `{n}` and a pad, expanded once into rows (ADR-0064 decision 7), shared
// with the Plan page's "Add several" locations.
import { expand } from "../../naming";

export type SiteVlan = components["schemas"]["SiteVlan"];
export type SiteVlanSpace = components["schemas"]["SiteVlanSpace"];
export type VlanNameEntry = components["schemas"]["VlanNameEntry"];

/** 802.1Q's range, the same bound the server's three tables hold. */
export const VLAN_MIN = 1;
export const VLAN_MAX = 4094;

/** The tag space as the views read it: one lookup per tag, built once from
 * the server's answer. `referencedUnnamed` is the server's word (decision
 * 4) and is never recomputed here from the fields beside it. */
export interface Space {
  customerId: string;
  byTag: Map<number, SiteVlan>;
  /** Tags that are named or referenced — the List view's population. */
  present: number[];
}

export function spaceOf(s: SiteVlanSpace): Space {
  const byTag = new Map<number, SiteVlan>();
  for (const v of s.vlans) byTag.set(v.vlan, v);
  return { customerId: s.customer_id, byTag, present: s.vlans.map((v) => v.vlan).sort((a, b) => a - b) };
}

export const nameOf = (space: Space, id: number): string => space.byTag.get(id)?.name ?? "";
export const isNamed = (space: Space, id: number): boolean => nameOf(space, id) !== "";
/** Whether a port, a network or a per-unit access row holds the tag — the
 * three references ADR-0139 decision 3 counts (#952: the PAN was missing
 * here while the server counted it). */
export const isReferenced = (space: Space, id: number): boolean => { const v = space.byTag.get(id); return !!v && (v.ports.length > 0 || v.networks.length > 0 || v.pans.length > 0); };
export const isReferencedUnnamed = (space: Space, id: number): boolean => space.byTag.get(id)?.referenced_unnamed ?? false;

/** A reference as a pill reads it: `5c17834a9ac0:Ethernet3 (tagged)` for a
 * port, the network's name for a network, and a per-unit access row by its
 * unit, else its own name (ADR-0139 decision 3, #952). A planned switch's
 * port (ADR-0069) is a reference too — the tag is reserved by the plan — and
 * reads by the planned device's name, marked planned, until an installer
 * binds a serial; then by the serial, as any other port. */
export function referenceLabels(v: SiteVlan | undefined): { kind: "port" | "network" | "pan" | "voice"; label: string }[] {
  if (!v) return [];
  return [
    ...v.ports.map((p) => ({ kind: "port" as const, label: `${portOwner(p)}:${p.port}${p.tagged ? " tagged" : " untagged"}` })),
    ...v.networks.map((n) => ({ kind: "network" as const, label: n.name })),
    ...v.pans.map((p) => ({ kind: "pan" as const, label: p.unit_name ?? p.name })),
    // A switch's voice VLAN (ADR-0369 decision 1a): the id found, or the one set.
    ...(v.voice ?? []).map((r) => ({ kind: "voice" as const, label: `${r.serial} voice${r.intended ? " (set)" : ""}` })),
  ];
}

/** The switch a port reference names: its serial, or the planned device's
 * name while it has none. */
export function portOwner(p: SiteVlan["ports"][number]): string {
  if (p.serial) return p.serial;
  return `${p.planned_name ?? "a planned switch"} (planned)`;
}

export const pad4 = (id: number): string => String(id).padStart(4, "0");

/** The hundred a tag falls in: 250 → 200. */
export const blockOf = (id: number): number => Math.floor(id / 100) * 100;

export const STATES = ["any", "named", "unnamed", "referenced", "referenced-unnamed"] as const;
export type State = (typeof STATES)[number];
export const STATE_LABEL: Record<State, string> = {
  any: "Any", named: "Named", unnamed: "Unnamed", referenced: "Referenced", "referenced-unnamed": "Referenced and unnamed",
};

export interface Filter { q: string; state: State; block: number | "any" }
export const EMPTY_FILTER: Filter = { q: "", state: "any", block: "any" };

/** What the search box says: a range (`600-615`), or a needle matched
 * against the tag, the name and the references. A bare number is a needle
 * too, so `25` finds 25, 250 and 2500 rather than only one row. */
export function parseSearch(q: string): { range?: [number, number]; needle: string } {
  const t = q.trim();
  const m = t.match(/^(\d{1,4})\s*[-–]\s*(\d{1,4})$/);
  if (m) {
    const lo = Math.max(VLAN_MIN, Math.min(Number(m[1]), Number(m[2])));
    const hi = Math.min(VLAN_MAX, Math.max(Number(m[1]), Number(m[2])));
    return { range: [lo, hi], needle: "" };
  }
  return { needle: t.toLowerCase() };
}

/** The range the filter bar names, for the strip's window: the search
 * range when there is one, else the block. */
export function filterRange(f: Filter): [number, number] | undefined {
  const { range } = parseSearch(f.q);
  if (range) return range;
  if (f.block !== "any") return [Math.max(VLAN_MIN, f.block), Math.min(VLAN_MAX, f.block + 99)];
  return undefined;
}

/** One predicate for the three views (§3): a tag passes the filter or it
 * does not, whichever view is asking. */
export function predicate(space: Space, f: Filter): (id: number) => boolean {
  const { range, needle } = parseSearch(f.q);
  return (id) => {
    const named = isNamed(space, id);
    const referenced = isReferenced(space, id);
    if (f.state === "named" && !named) return false;
    if (f.state === "unnamed" && named) return false;
    if (f.state === "referenced" && !referenced) return false;
    if (f.state === "referenced-unnamed" && !isReferencedUnnamed(space, id)) return false;
    if (f.block !== "any" && blockOf(id) !== f.block) return false;
    if (range) return id >= range[0] && id <= range[1];
    if (needle) {
      if (String(id).includes(needle)) return true;
      if (nameOf(space, id).toLowerCase().includes(needle)) return true;
      return referenceLabels(space.byTag.get(id)).some((r) => r.label.toLowerCase().includes(needle));
    }
    return true;
  };
}

/** Every tag, in order, that passes the filter. */
export function allMatching(space: Space, f: Filter): number[] {
  const p = predicate(space, f);
  const out: number[] = [];
  for (let id = VLAN_MIN; id <= VLAN_MAX; id++) if (p(id)) out.push(id);
  return out;
}

/** The List view's population: named or referenced, and passing the filter. */
export function presentMatching(space: Space, f: Filter): number[] {
  const p = predicate(space, f);
  return space.present.filter(p);
}

/** The acts the `Action` control offers, all of which **write a name**. The
 * deletion is not among them (#823): it has its own control in the bar, so a
 * list whose every entry writes no longer carries the one entry that destroys.
 * `clear` is still an `Op` — it is the plan that control computes. */
export const OPS = ["pattern", "prefix", "suffix", "replace"] as const;
export type Op = (typeof OPS)[number] | "clear";
// `clear` is a deletion and is named as one (#780). A row is deleted rather
// than blanked, so "delete" is what happens; "clear" was read as clearing a
// VLAN off the equipment, which nothing on this page does.
export const OP_LABEL: Record<Op, string> = { pattern: "Name by pattern", prefix: "Add a prefix", suffix: "Add a suffix", replace: "Find & replace", clear: "Delete names" };
export const OP_PLACEHOLDER: Record<Op, string> = { pattern: "Retail {n}", prefix: "Bldg A · ", suffix: " (legacy)", replace: "find", clear: "" };

/** Whether an op deletes rows rather than writing names. One predicate, so
 * the bar's confirmation and its wording cannot disagree about which act is
 * the destructive one. */
export function deletes(kind: Op): boolean { return kind === "clear"; }

/** Whether a value the `Action` control could hold is one it offers. The
 * control is rendered from `OPS`, so this is what a test asks rather than
 * asserting against a list it has copied. */
export function offeredByAction(kind: Op): boolean { return (OPS as readonly string[]).includes(kind); }

export interface BulkOp { kind: Op; a: string; b: string }

/** The plan of a bulk action over a sorted selection: the entries a put
 * would carry, in tag order, and nothing for a tag the action does not
 * change. A prefix, suffix or replace touches only tags that have a name;
 * a clear touches only tags that have one; a pattern names every tag
 * selected. **The preview and the put both read this list** — the bar shows
 * its head and sends it whole — which is what makes "the preview equals
 * the set sent" a property rather than a hope. */
export function planOp(op: BulkOp, ids: number[], space: Space): VlanNameEntry[] {
  const out: VlanNameEntry[] = [];
  const sorted = [...ids].sort((a, b) => a - b);
  sorted.forEach((id, i) => {
    const cur = nameOf(space, id);
    let next: string | undefined;
    switch (op.kind) {
      case "pattern": if (op.a.trim()) next = expand(op.a, id, i + 1); break;
      case "prefix": if (cur && op.a) next = op.a + cur; break;
      case "suffix": if (cur && op.a) next = cur + op.a; break;
      case "replace": if (cur && op.a && cur.includes(op.a)) next = cur.split(op.a).join(op.b); break;
      case "clear": if (cur) next = ""; break;
    }
    if (next === undefined || next === cur) return;
    out.push({ vlan: id, name: next });
  });
  return out;
}

/** The inverse of a plan: for every tag the plan changes, the value that tag
 * held before it. A prior name restores the row; an empty name deletes the
 * row the act created, which is the same put in the other direction — so one
 * function inverts every op and not only the deleting one (#781).
 *
 * **Read it before the put, never after.** It is computed from the space the
 * plan was computed against; once the put lands and the page reloads, the
 * prior names are gone and there is nothing left to derive it from.
 *
 * It is a second write and not a revert: it goes through the same
 * `PUT /customers/{id}/vlans` and writes its own audit event, so a customer's
 * log shows two acts (ADR-0064 decision 6). */
export function inverseOf(plan: VlanNameEntry[], space: Space): VlanNameEntry[] {
  return plan.map((e) => ({ vlan: e.vlan, name: nameOf(space, e.vlan) }));
}

/** The sentence the bar shows for a plan: how many change, the first three
 * results, and what it does not touch. */
export function previewOf(op: BulkOp, plan: VlanNameEntry[], selected: number): string {
  if (plan.length === 0) {
    if (op.kind === "pattern") return "Enter a pattern";
    if (op.kind === "clear") return "Nothing selected has a name";
    return `Nothing would change. Only named VLANs take a ${op.kind === "replace" ? "replacement" : op.kind}.`;
  }
  const head = plan.slice(0, 3).map((e) => (e.name ? e.name : `${e.vlan} deleted`)).join(", ");
  const untouched = selected - plan.length;
  return `${plan.length} of ${selected} would change: ${head}${plan.length > 3 ? " …" : ""}${untouched > 0 ? ` · ${untouched} untouched` : ""}`;
}

// --- zoom (#511): view state, never a filter and never a selection ----------------------

/** The strip's window: the tags it shows, inclusive. The whole is [1, 4094]. */
export type Window = [number, number];
export const WHOLE: Window = [VLAN_MIN, VLAN_MAX];

/** A window inside the tag space, at least `MIN_WINDOW` tags wide so that a
 * zoom never lands on a strip of one cell. */
export const MIN_WINDOW = 20;
export function clampWindow([lo, hi]: Window): Window {
  let a = Math.max(VLAN_MIN, Math.floor(Math.min(lo, hi)));
  let b = Math.min(VLAN_MAX, Math.ceil(Math.max(lo, hi)));
  if (b - a + 1 < MIN_WINDOW) {
    const mid = (a + b) / 2;
    a = Math.max(VLAN_MIN, Math.round(mid - MIN_WINDOW / 2));
    b = Math.min(VLAN_MAX, a + MIN_WINDOW - 1);
    a = Math.max(VLAN_MIN, b - MIN_WINDOW + 1);
  }
  return [a, b];
}

/** The window that shows a selection with a readable margin on either side:
 * a quarter of the span, and at least ten tags, so 600–615 reads 590–625 and
 * a single tag sits in a window of twenty. A selection that spans the whole
 * space is the whole. */
export function windowForSelection(selected: Iterable<number>): Window | undefined {
  let lo = Infinity, hi = -Infinity;
  for (const id of selected) { if (id < lo) lo = id; if (id > hi) hi = id; }
  if (lo === Infinity) return undefined;
  const margin = Math.max(10, Math.ceil((hi - lo + 1) / 4));
  return clampWindow([lo - margin, hi + margin]);
}

/** The window after one wheel step around `at` (a tag): the tag under the
 * pointer stays under it, the span shrinks by `factor` (< 1 zooms in). */
export function zoomWindow([lo, hi]: Window, at: number, factor: number): Window {
  const span = hi - lo + 1;
  const next = Math.max(MIN_WINDOW, Math.min(VLAN_MAX, Math.round(span * factor)));
  if (next === span) return [lo, hi];
  const frac = (at - lo) / span;
  const a = Math.round(at - frac * next);
  return clampWindow([a, a + next - 1]);
}

/** The strip's nominal width on screen, in CSS px: the number a tick's width
 * is derived from, constant so the same window draws the same ticks on every
 * machine and in a test. A real strip is a little wider or narrower; the tick
 * is then a little more or less than two pixels, never under one. */
export const STRIP_PX = 1100;
/** A tick's width in tag units for a window: at least one tag, and at least
 * two screen pixels at the nominal width — so ticks separate as the window
 * narrows and a lone tag is still visible at the whole. */
export function tickWidth([lo, hi]: Window): number {
  return Math.max(1, Math.ceil(((hi - lo + 1) * 2) / STRIP_PX));
}

/** The axis labels for a window: five, from `lo` to `hi`. */
export function axisLabels([lo, hi]: Window): number[] {
  const span = hi - lo;
  return [0, 1, 2, 3, 4].map((i) => Math.round(lo + (span * i) / 4));
}

/** The tag at a fraction of the strip's width, through the window. */
export function tagAtFraction([lo, hi]: Window, frac: number): number {
  return Math.min(hi, Math.max(lo, Math.round(lo + frac * (hi - lo + 1) - 0.5)));
}

/** The Map's levels: how many tags a row carries (#816). Each is a divisor
 * of a hundred, so a row stays a round fraction of a block and a row label
 * names a round tag — 50 is half a block, 25 a quarter.
 *
 * The level is the *only* knob, and that is what keeps the Map inside its
 * panel: the cell is computed from the width a row has to share, so fewer
 * columns is a larger cell and the grid grows **downward**. The Map never
 * scrolls sideways at any level. Before #816 the level multiplied a fixed
 * 100-column canvas, so every level above the first put the right-hand tags
 * off-screen behind a horizontal scrollbar. */
export const COL_LEVELS = [100, 50, 25, 20] as const;
export type Cols = (typeof COL_LEVELS)[number];

/** A step towards a larger cell (`1`) or a denser grid (`-1`). Larger cells
 * are *fewer* columns, so a step in is a step down this list. */
export function colsStep(c: Cols, dir: 1 | -1): Cols {
  const i = COL_LEVELS.indexOf(c);
  return COL_LEVELS[Math.max(0, Math.min(COL_LEVELS.length - 1, i + dir))] ?? c;
}

/** The smallest cell worth drawing, and the largest worth growing to. Below
 * the first a cell is too small to aim at; above the second the grid is
 * taller than it is useful without telling anybody more. */
export const MIN_CELL = 7, MAX_CELL = 44;

/** The cell size a first view aims for. Smaller than a touch target on
 * purpose: the Map is a picture of four thousand tags, and a grid drawn at
 * forty-four pixels a cell is nine screens tall. Pointing at one cell is the
 * keyboard's and the tooltip's job, not the default level's. */
export const COMFORT_CELL = 14;

/** What a cell is worth at this width, before it is judged. `width` is the
 * whole canvas, `gutter` the strip the row labels sit in. */
export function cellFor(width: number, cols: Cols, gutter: number, gap: number): number {
  const each = Math.floor((width - gutter) / cols) - gap;
  return Math.min(MAX_CELL, Math.max(MIN_CELL, each));
}

/** The level a width is first shown at: the densest grid whose cell is still
 * comfortable to aim at, and failing that the densest that is legible at all.
 *
 * It is *not* the densest that fits, which is what a fit-to-width rule would
 * give and is how the Map used to open — 100 columns in a nine-hundred-pixel
 * panel is an eight-pixel cell, which fits and cannot be read. A narrow
 * window therefore opens at fewer columns rather than at unreadable ones,
 * which is what carries the Map onto a tablet and a phone. */
export function fitCols(width: number, gutter: number, gap: number): Cols {
  const comfortable = COL_LEVELS.find((c) => cellFor(width, c, gutter, gap) >= COMFORT_CELL);
  if (comfortable) return comfortable;
  const legible = COL_LEVELS.find((c) => (width - gutter) / c - gap >= MIN_CELL);
  return legible ?? COL_LEVELS[COL_LEVELS.length - 1]!;
}

// --- a set of tags, read by the customer's names (#590) ---------------------------------

/** A contiguous run of tags, inclusive: `[3000, 4000]` is a thousand and one. */
export type Run = [number, number];

/** The contiguous runs of a set of tags, ascending: `[10, 20, 3000…4000]`
 * is `[[10, 10], [20, 20], [3000, 4000]]`. Repeats fold. */
export function runsOf(tags: Iterable<number>): Run[] {
  const sorted = [...new Set(tags)].sort((a, b) => a - b);
  const out: Run[] = [];
  for (const t of sorted) {
    const last = out[out.length - 1];
    if (last && t === last[1] + 1) last[1] = t;
    else out.push([t, t]);
  }
  return out;
}

/** A run in words: `30`, or `3000–4000`. */
export const runWords = ([lo, hi]: Run): string => (lo === hi ? String(lo) : `${lo}–${hi}`);
/** A set of tags as its runs: `10, 20, 3000–4000`. */
export const runsWords = (tags: Iterable<number>): string => runsOf(tags).map(runWords).join(", ");

/** A name the customer gave to one or more tags, as the chooser lists it:
 * the tags ascending and their runs, so *Rooms* reads `3000–4000` and a
 * group with a gap reads its runs. */
export interface NameGroup { name: string; tags: number[]; runs: Run[] }

/** One group per name, names in natural order. A name several tags share
 * is one group; the tags nobody named are not here. */
export function nameGroups(space: Space): NameGroup[] {
  const byName = new Map<string, number[]>();
  for (const tag of space.present) {
    const name = nameOf(space, tag);
    if (name) byName.set(name, [...(byName.get(name) ?? []), tag]);
  }
  return [...byName.entries()]
    .map(([name, tags]) => { const t = [...new Set(tags)].sort((a, b) => a - b); return { name, tags: t, runs: runsOf(t) }; })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
}

/** A set of tags read by the customer's names: the groups of more than one
 * tag that the set holds whole, in name order, then every other tag. A
 * group of one tag is a label on a number, not a way of saying a thousand
 * of them, so it stays a number here; a group the set holds in part is
 * its numbers too, since *Rooms* would be claiming tags the set lacks. */
export function setByName(space: Space | undefined, tags: readonly number[]): { groups: NameGroup[]; rest: number[] } {
  const held = new Set(tags);
  const groups: NameGroup[] = [];
  const covered = new Set<number>();
  if (space) {
    for (const g of nameGroups(space)) {
      if (g.tags.length < 2 || !g.tags.every((t) => held.has(t))) continue;
      groups.push(g);
      for (const t of g.tags) covered.add(t);
    }
  }
  return { groups, rest: [...held].filter((t) => !covered.has(t)).sort((a, b) => a - b) };
}

/** A set of tags in words, by its named groups and runs: `Rooms (1001), 10,
 * 20` rather than a thousand numbers. Without a space, the runs alone. */
export function setWords(space: Space | undefined, tags: readonly number[]): string {
  const { groups, rest } = setByName(space, tags);
  return [...groups.map((g) => `${g.name} (${g.tags.length})`), ...(rest.length ? [runsWords(rest)] : [])].join(", ");
}
